/**
 * Menu bar slot helpers — aligned with TabManager barSlots / Menu barItems.
 * A bar slot is either a top-level tab or a group (tabs sharing group_key).
 */

export function getTabGroupKey(tab) {
  return (tab?.config?.group_key ?? tab?.group_key ?? tab?.appGroupKey)?.trim() || '';
}

export function getTabGroupLabel(tab) {
  const gk = getTabGroupKey(tab);
  const gl = (tab?.config?.group_label ?? tab?.group_label ?? tab?.appGroupLabel)?.trim();
  if (gl) return gl;
  if (!gk) return '';
  return gk.replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Build ordered bar slots from a tab list (array order = nav order).
 * @param {Array<object>} tabs
 * @returns {Array<{ type: 'tab', tab } | { type: 'group', key, label, tabs }>}
 */
export function buildBarSlotsFromTabs(tabs) {
  const ordered = Array.isArray(tabs) ? tabs : [];
  const slots = [];
  const groupSeen = new Set();

  ordered.forEach((tab) => {
    const gk = getTabGroupKey(tab);
    if (!gk) {
      slots.push({ type: 'tab', tab });
      return;
    }
    if (groupSeen.has(gk)) return;
    groupSeen.add(gk);
    const orderIndex = new Map(ordered.map((t, i) => [String(t.id), i]));
    const groupTabs = ordered
      .filter((t) => getTabGroupKey(t) === gk)
      .sort(
        (a, b) =>
          (orderIndex.get(String(a.id)) ?? 9999) - (orderIndex.get(String(b.id)) ?? 9999)
      );
    slots.push({
      type: 'group',
      key: gk,
      label: getTabGroupLabel(groupTabs[0]) || gk,
      tabs: groupTabs,
    });
  });

  return slots;
}

/** Flatten bar slots to tab list (menu bar order, group members contiguous). */
export function flattenBarSlotsToTabs(barSlots) {
  return (barSlots || []).flatMap((slot) =>
    slot.type === 'tab' ? [slot.tab] : slot.tabs || []
  );
}

export function removeTabFromBarSlots(barSlots, tabId) {
  const id = String(tabId);
  return (barSlots || [])
    .map((slot) => {
      if (slot.type === 'tab') {
        return String(slot.tab.id) === id ? null : slot;
      }
      const tabs = (slot.tabs || []).filter((t) => String(t.id) !== id);
      if (tabs.length === 0) return null;
      return { ...slot, tabs };
    })
    .filter(Boolean);
}

export function addTabToBarSlots(barSlots, tab) {
  const gk = getTabGroupKey(tab);
  if (!gk) {
    return [...(barSlots || []), { type: 'tab', tab }];
  }
  const list = [...(barSlots || [])];
  const idx = list.findIndex((s) => s.type === 'group' && s.key === gk);
  if (idx >= 0) {
    return list.map((s, i) =>
      i === idx ? { ...s, tabs: [...(s.tabs || []), tab] } : s
    );
  }
  return [
    ...list,
    {
      type: 'group',
      key: gk,
      label: getTabGroupLabel(tab) || gk,
      tabs: [tab],
    },
  ];
}
